import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import Login from './Login/Login';
//@ts-ignore
import classes from './AppRouter.module.scss';
import UnregisteredHome from './UnregisteredHome/UnregisteredHome';
import Account from './Account/Account';
import MainPage from './MainPage/MainPage';
import Projects from './Projects/Projects';
import ProjectOpen from './Projects/ProjectOpen/ProjectOpen';
import Field from './Field/Field';
import CompletedMonitorings from './ComplitedMonitoring/ComplitedMonitoring';
import ReportsWells from './ComplitedMonitoring/Reports/ReportsWells';
import Wells from './Wells/Wells';
import Statistics from './Statistics/Statistics';
import ProtectedProjectRoute from './Projects/ProjectOpen/ProjectOpenContent/children/ProtectedProjectRoute';
import Directories from './Directories/Directories';

// Компонент для защищенных маршрутов
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// Компонент для проверки регистрации
const RegistrationRoute = ({ children }: { children: React.ReactNode }) => {
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  
  let isRegistered = userInfo?.is_registered;
  if (isRegistered === undefined) {
    const storedUserInfo = localStorage.getItem('userInfo');
    if (storedUserInfo) {
      try {
        const parsedUserInfo = JSON.parse(storedUserInfo);
        isRegistered = parsedUserInfo.is_registered;
      } catch (error) {
        console.error('Ошибка парсинга userInfo из localStorage:', error);
        isRegistered = false;
      }
    }
  }

  if (isRegistered) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
};

// Компонент для проверки уровня доступа
const AccessLevelRoute = ({ children, requiredLevel = 1 }: { children: React.ReactNode; requiredLevel?: number }) => {
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  
  let accessLevel: any = userInfo?.role?.access_level;
  
  if (accessLevel === undefined) {
    const storedUserInfo = localStorage.getItem('userInfo');
    if (storedUserInfo) {
      try {
        const parsedUserInfo = JSON.parse(storedUserInfo);
        accessLevel = parsedUserInfo.role?.access_level;
      } catch (error) {
        console.error('Ошибка парсинга userInfo из localStorage:', error);
        accessLevel = 0;
      }
    }
  }

  // Если уровень доступа меньше требуемого - перенаправляем на проекты
  if (accessLevel < requiredLevel) {
    return <Navigate to="/project" replace />;
  }
  
  return <>{children}</>;
};

// Компонент для проверки доступа к основным маршрутам
const MainRoute = ({ children }: { children: React.ReactNode }) => {
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  
  let isRegistered = userInfo?.is_registered;
  if (isRegistered === undefined) {
    const storedUserInfo = localStorage.getItem('userInfo');
    if (storedUserInfo) {
      try {
        const parsedUserInfo = JSON.parse(storedUserInfo);
        isRegistered = parsedUserInfo.is_registered;
      } catch (error) {
        console.error('Ошибка парсинга userInfo из localStorage:', error);
        isRegistered = false;
      }
    }
  }

  // Если пользователь не зарегистрирован - перенаправляем на unregistered
  if (!isRegistered) {
    return <Navigate to="/unregistered" replace />;
  }
  
  return <>{children}</>;
};

// Компонент для публичных маршрутов (только для неавторизованных)
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  
  if (isAuthenticated) {
    const storedUserInfo = localStorage.getItem('userInfo');
    let isRegistered = false;
    
    if (storedUserInfo) {
      try {
        const parsedUserInfo = JSON.parse(storedUserInfo);
        isRegistered = parsedUserInfo.is_registered;
      } catch (error) {
        console.error('Ошибка парсинга userInfo из localStorage:', error);
      }
    }
    
    return <Navigate to={isRegistered ? "/" : "/unregistered"} replace />;
  }
  
  return <>{children}</>;
};

// Специальный маршрут для Account - доступен всем авторизованным пользователям
const AccountRoute = ({ children }: { children: React.ReactNode }) => {
  return <>{children}</>;
};

// Компонент для проверки доступа к страницам проекта
const ProjectPageRoute = ({ children, pageType }: { children: React.ReactNode; pageType: 'basic' | 'advanced' }) => {
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  
  let accessLevel = userInfo?.role?.access_level;
  
  if (accessLevel === undefined) {
    const storedUserInfo = localStorage.getItem('userInfo');
    if (storedUserInfo) {
      try {
        const parsedUserInfo = JSON.parse(storedUserInfo);
        accessLevel = parsedUserInfo.role?.access_level;
      } catch (error) {
        console.error('Ошибка парсинга userInfo из localStorage:', error);
        accessLevel = 0;
      }
    }
  }

  // Для access_level = 0 разрешаем только базовые страницы
  if (accessLevel === 0 && pageType === 'advanced') {
    return <Navigate to="/project" replace />;
  }
  
  return <>{children}</>;
};

const AppRouter = () => {
  return (
    <div className={classes.main}>
      <Routes>
        {/* Публичные маршруты - доступны только без авторизации */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        
        {/* Unregistered - доступен только для незарегистрированных пользователей */}
        <Route
          path="/unregistered"
          element={
            <ProtectedRoute>
              <RegistrationRoute>
                <UnregisteredHome />
              </RegistrationRoute>
            </ProtectedRoute>
          }
        />
        
        {/* Account - доступен всем авторизованным пользователям */}
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <AccountRoute>
                <Account />
              </AccountRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/1"
          element={
            <ProtectedRoute>
              <AccountRoute>
                <Account />
              </AccountRoute>
            </ProtectedRoute>
          }
        />
        
        {/* Основные маршруты - доступны только для зарегистрированных пользователей */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainRoute>
                <MainPage />
              </MainRoute>
            </ProtectedRoute>
          }
        />
        
        {/* Проекты - доступны всем зарегистрированным пользователям (включая access_level = 0) */}
        <Route
          path="/project"
          element={
            <ProtectedRoute>
              <MainRoute>
                <Projects />
              </MainRoute>
            </ProtectedRoute>
          }
        />
        
        {/* Базовые страницы проекта - доступны всем (включая access_level = 0) */}
        <Route
          path="/project/:projectUuid"
          element={
            <ProtectedRoute>
              <MainRoute>
                <ProjectPageRoute pageType="basic">
                  <ProtectedProjectRoute>
                    <ProjectOpen />
                  </ProtectedProjectRoute>
                </ProjectPageRoute>
              </MainRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:projectUuid/results"
          element={
            <ProtectedRoute>
              <MainRoute>
                <ProjectPageRoute pageType="basic">
                  <ProtectedProjectRoute>
                    <ProjectOpen />
                  </ProtectedProjectRoute>
                </ProjectPageRoute>
              </MainRoute>
            </ProtectedRoute>
          }
        />
        
        {/* Расширенные страницы проекта - требуют access_level >= 1 */}
        <Route
          path="/project/:projectUuid/chromatogram"
          element={
            <ProtectedRoute>
              <MainRoute>
                <ProjectPageRoute pageType="advanced">
                  <ProtectedProjectRoute>
                    <ProjectOpen />
                  </ProtectedProjectRoute>
                </ProjectPageRoute>
              </MainRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:projectUuid/wells"
          element={
            <ProtectedRoute>
              <MainRoute>
                <ProjectPageRoute pageType="advanced">
                  <ProtectedProjectRoute>
                    <ProjectOpen />
                  </ProtectedProjectRoute>
                </ProjectPageRoute>
              </MainRoute>
            </ProtectedRoute>
          }
        />
        
        {/* Маршруты мониторинга с проверкой стадий */}
        <Route
          path="/project/:projectUuid/monitoring"
          element={
            <ProtectedRoute>
              <MainRoute>
                <ProjectPageRoute pageType="advanced">
                  <ProtectedProjectRoute requiredStage={2}>
                    <ProjectOpen />
                  </ProtectedProjectRoute>
                </ProjectPageRoute>
              </MainRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:projectUuid/monitoring/2"
          element={
            <ProtectedRoute>
              <MainRoute>
                <ProjectPageRoute pageType="advanced">
                  <ProtectedProjectRoute requiredStage={2}>
                    <ProjectOpen />
                  </ProtectedProjectRoute>
                </ProjectPageRoute>
              </MainRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:projectUuid/monitoring/3"
          element={
            <ProtectedRoute>
              <MainRoute>
                <ProjectPageRoute pageType="advanced">
                  <ProtectedProjectRoute requiredStage={3}>
                    <ProjectOpen />
                  </ProtectedProjectRoute>
                </ProjectPageRoute>
              </MainRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:projectUuid/monitoring/4"
          element={
            <ProtectedRoute>
              <MainRoute>
                <ProjectPageRoute pageType="advanced">
                  <ProtectedProjectRoute requiredStage={4}>
                    <ProjectOpen />
                  </ProtectedProjectRoute>
                </ProjectPageRoute>
              </MainRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:projectUuid/monitoring/5"
          element={
            <ProtectedRoute>
              <MainRoute>
                <ProjectPageRoute pageType="advanced">
                  <ProtectedProjectRoute requiredStage={5}>
                    <ProjectOpen />
                  </ProtectedProjectRoute>
                </ProjectPageRoute>
              </MainRoute>
            </ProtectedRoute>
          }
        />
        
        {/* Месторождения - требуют access_level >= 1 */}
        <Route
          path="/field"
          element={
            <ProtectedRoute>
              <MainRoute>
                <AccessLevelRoute requiredLevel={1}>
                  <Field />
                </AccessLevelRoute>
              </MainRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/field/:fieldUuid"
          element={
            <ProtectedRoute>
              <MainRoute>
                <AccessLevelRoute requiredLevel={1}>
                  <Field />
                </AccessLevelRoute>
              </MainRoute>
            </ProtectedRoute>
          }
        />
        
        {/* Отчеты - требуют access_level >= 1 */}
        <Route
          path="/report"
          element={
            <ProtectedRoute>
              <MainRoute>
                <AccessLevelRoute requiredLevel={1}>
                  <CompletedMonitorings />
                </AccessLevelRoute>
              </MainRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/report/:reportUuid"
          element={
            <ProtectedRoute>
              <MainRoute>
                <AccessLevelRoute requiredLevel={1}>
                  <ReportsWells />
                </AccessLevelRoute>
              </MainRoute>
            </ProtectedRoute>
          }
        />
        
        {/* Скважины - требуют access_level >= 1 */}
        <Route
          path="/wells"
          element={
            <ProtectedRoute>
              <MainRoute>
                <AccessLevelRoute requiredLevel={1}>
                  <Wells />
                </AccessLevelRoute>
              </MainRoute>
            </ProtectedRoute>
          }
        />
        {/* Справочник - требуют access_level >= 1 */}
        <Route
          path="/directory"
          element={
            <ProtectedRoute>
              <MainRoute>
                <AccessLevelRoute requiredLevel={1}>
                  <Directories />
                </AccessLevelRoute>
              </MainRoute>
            </ProtectedRoute>
          }
        />
        
        {/* Статистика - требует access_level >= 1 */}
        <Route
          path="/statistics"
          element={
            <ProtectedRoute>
              <MainRoute>
                <AccessLevelRoute requiredLevel={1}>
                  <Statistics />
                </AccessLevelRoute>
              </MainRoute>
            </ProtectedRoute>
          }
        />
        
        {/* Маршрут по умолчанию */}
        <Route
          path="*"
          element={<Navigate to="/login" replace />}
        />
      </Routes>
    </div>
  );
};

export default AppRouter;